import { useState } from "react";
import { useIsMobile } from "@/hooks/use-mobile";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import { useToast } from "@/hooks/use-toast";
import { Copy, MessageCircle, Share2, Image, Loader2 } from "lucide-react";
import { XIcon } from "@/components/ui/icons/XIcon";
import { generateShareImage, downloadShareImage } from "@/utils/shareImageGenerator";
import { ShareTroubleshoot } from "@/components/share/ShareTroubleshoot";

interface InGameShareModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  puzzleNumber: number;
  guessCount: number;
  results: Array<Array<"correct" | "present" | "absent">>;
}

export function InGameShareModal({
  open,
  onOpenChange,
  puzzleNumber,
  guessCount,
  results,
}: InGameShareModalProps) {
  const isMobile = useIsMobile();
  const { toast } = useToast();
  const [isGeneratingImage, setIsGeneratingImage] = useState(false);

  const generateProgressGrid = () => {
    return results
      .map((row) =>
        row
          .map((status) => {
            if (status === "correct") return "🩷";  // pink heart
            if (status === "present") return "🟪";  // purple square
            return "🤎";  // brown heart for better visibility
          })
          .join("")
      )
      .join("\n");
  };

  const getShareText = () => {
    const grid = generateProgressGrid();
    return `WordPop #${puzzleNumber} — ${guessCount}/6 in progress...\n\n${grid}\n\nwordpop.games`;
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(getShareText());
      toast({
        title: "Copied!",
        description: "Progress copied to clipboard",
      });
      onOpenChange(false);
    } catch {
      toast({
        title: "Couldn't copy",
        description: "Please try again",
        variant: "destructive",
      });
    }
  };

  const handleCopyImage = async () => {
    setIsGeneratingImage(true);
    try {
      const dataUrl = await generateShareImage({
        puzzleNumber,
        won: false,
        attempts: guessCount,
        results,
        inProgress: true,
      });

      // Try to copy image to clipboard
      try {
        const response = await fetch(dataUrl);
        const blob = await response.blob();
        await navigator.clipboard.write([
          new ClipboardItem({ "image/png": blob }),
        ]);
        toast({
          title: "Image copied!",
          description: "Progress image copied to clipboard",
        });
        onOpenChange(false);
      } catch {
        // Fallback to download if clipboard fails
        downloadShareImage(dataUrl, `wordpop-${puzzleNumber}-progress.png`);
        toast({
          title: "Image saved!",
          description: "Progress image downloaded",
        });
        onOpenChange(false);
      }
    } catch (err) {
      console.error("Failed to generate image:", err);
      toast({
        title: "Couldn't generate image",
        description: "Please try again",
        variant: "destructive",
      });
    } finally {
      setIsGeneratingImage(false);
    }
  };

  const handleWhatsApp = () => {
    const text = encodeURIComponent(getShareText());
    window.open(`https://wa.me/?text=${text}`, "_blank");
    onOpenChange(false);
  };

  const handleSMS = () => {
    const text = encodeURIComponent(getShareText());
    const isIOS = /iPhone|iPad|iPod/.test(navigator.userAgent);
    const smsUrl = isIOS ? `sms:&body=${text}` : `sms:?body=${text}`;
    window.open(smsUrl, "_blank");
    onOpenChange(false);
  };

  const handleTwitter = () => {
    const text = encodeURIComponent(getShareText());
    const url = encodeURIComponent("https://wordpop.games/play");
    window.open(
      `https://twitter.com/intent/tweet?text=${text}&url=${url}`,
      "_blank"
    );
    onOpenChange(false);
  };

  const handleFacebook = () => {
    const shareUrl = `https://wordpop.games/play?puzzle=${puzzleNumber}`;
    const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;
    
    // Log for debugging stale cache issues
    console.log('[Share] Facebook share triggered, URL:', facebookUrl);
    
    // Verify correct endpoint
    if (!facebookUrl.includes('sharer/sharer.php')) {
      console.error('[Share] ⚠️ Stale code detected!');
      toast({
        title: "Update required",
        description: "Please update the app to fix Facebook sharing",
        variant: "destructive",
      });
      return;
    }
    
    // On iOS, Facebook app interception causes "Internal App Misconfigured" error
    // Workaround: copy link and prompt user to paste in Facebook
    const isIOS = /iPhone|iPad|iPod/.test(navigator.userAgent);
    if (isIOS) {
      navigator.clipboard.writeText(shareUrl).then(() => {
        toast({
          title: "Link copied!",
          description: "Open Facebook and paste to share",
        });
      }).catch(() => {
        toast({
          title: "Couldn't copy",
          description: "Please try again",
          variant: "destructive",
        });
      });
    } else {
      window.open(facebookUrl, "_blank");
    }
    onOpenChange(false);
  };

  const handleLinkedIn = () => {
    const url = encodeURIComponent(
      `https://wordpop.games/play?puzzle=${puzzleNumber}`
    );
    window.open(
      `https://www.linkedin.com/sharing/share-offsite/?url=${url}`,
      "_blank"
    );
    onOpenChange(false);
  };

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: "WordPop Progress",
          text: getShareText(),
        });
        onOpenChange(false);
      } catch (err) {
        if ((err as Error).name !== "AbortError") {
          handleCopy();
        }
      }
    } else {
      handleCopy();
    }
  };

  const content = (
    <div className="flex flex-col gap-4 p-4">
      {/* Emoji grid preview */}
      <div className="bg-muted/50 rounded-lg p-4 text-center font-mono text-lg leading-relaxed">
        {generateProgressGrid().split("\n").map((row, i) => (
          <div key={i}>{row}</div>
        ))}
      </div>

      {/* Primary share actions */}
      <div className="grid grid-cols-3 gap-2">
        <Button variant="outline" onClick={handleCopy} className="flex-col gap-1 h-auto py-3">
          <Copy className="h-5 w-5" />
          <span className="text-xs">Copy</span>
        </Button>
        <Button 
          variant="outline" 
          onClick={handleCopyImage} 
          disabled={isGeneratingImage}
          className="flex-col gap-1 h-auto py-3"
        >
          {isGeneratingImage ? (
            <Loader2 className="h-5 w-5 animate-spin" />
          ) : (
            <Image className="h-5 w-5" />
          )}
          <span className="text-xs">Image</span>
        </Button>
        <Button variant="outline" onClick={handleWhatsApp} className="flex-col gap-1 h-auto py-3 text-[#25D366] hover:text-[#25D366]">
          <MessageCircle className="h-5 w-5" />
          <span className="text-xs">WhatsApp</span>
        </Button>
      </div>

      {/* Secondary share actions */}
      <div className="grid grid-cols-3 gap-2">
        <Button variant="outline" onClick={handleSMS} className="flex-col gap-1 h-auto py-3">
          <MessageCircle className="h-5 w-5" />
          <span className="text-xs">SMS</span>
        </Button>
        <Button variant="outline" onClick={handleTwitter} className="flex-col gap-1 h-auto py-3">
          <XIcon className="h-5 w-5" />
          <span className="text-xs">X</span>
        </Button>
        <Button variant="outline" onClick={handleFacebook} className="flex-col gap-1 h-auto py-3 text-[#1877F2] hover:text-[#1877F2]">
          <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
            <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
          </svg>
          <span className="text-xs">Facebook</span>
        </Button>
      </div>

      {/* Third row */}
      <div className="grid grid-cols-3 gap-2">
        <Button variant="outline" onClick={handleLinkedIn} className="flex-col gap-1 h-auto py-3 text-[#0A66C2] hover:text-[#0A66C2]">
          <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
            <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
          </svg>
          <span className="text-xs">LinkedIn</span>
        </Button>
      </div>

      {/* Native share button */}
      {typeof navigator !== "undefined" && navigator.share && (
        <Button onClick={handleNativeShare} className="w-full gap-2">
          <Share2 className="h-4 w-4" />
          Share
        </Button>
      )}

      {/* Troubleshoot link with version */}
      <ShareTroubleshoot compact showVersion />
    </div>
  );

  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={onOpenChange}>
        <DrawerContent>
          <DrawerHeader className="text-center">
            <DrawerTitle>Share Progress</DrawerTitle>
            <p className="text-sm text-muted-foreground">
              {guessCount}/6 guesses
            </p>
          </DrawerHeader>
          {content}
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Share Progress</DialogTitle>
          <p className="text-sm text-muted-foreground">
            {guessCount}/6 guesses
          </p>
        </DialogHeader>
        {content}
      </DialogContent>
    </Dialog>
  );
}
